import BackButton from '@/components/back-button/back-button';
import ContentContainerAuthPages from '@/components/layout-auth/components/content-container-auth-pages/content-container-auth-pages';
import FooterAuth from '@/components/layout-auth/components/footer-auth/footer-auth';
import FormContainerAuthPages from '@/components/layout-auth/components/form-container-auth-pages/form-container-auth-pages';
import ImageSectionAuthPages from '@/components/layout-auth/components/image-section-auth-pages/image-section-auth-pages';
import MainContentAuth from '@/components/layout-auth/components/main-content-auth/main-content-auth';
import NavigationBar from '@/components/layout/navigation-bar/navigation-bar';
import SubmitAccentButton from '@/components/submit-accent-button/submit-accent-button';
import Troubleshoot from '@/components/troubleshoot/Troubleshoot';
import { ROUTE_SIGN_UP } from '@/constants/routes';
import validationService from '@/services/validation.service';
import { getRoute } from '@/utils';
import { FormControl, FormErrorMessage } from '@chakra-ui/react';
import { Box, Divider, Flex, Grid, InlineAlert, Input, Link, Text } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import image1000 from '../images/sign-in-1000w.avif';
import image1200 from '../images/sign-in-1200w.avif';
import image1600 from '../images/sign-in-1600w.avif';
import image2000 from '../images/sign-in-2000w.avif';
import image400 from '../images/sign-in-400w.avif';
import image600 from '../images/sign-in-600w.avif';
import image800 from '../images/sign-in-800w.avif';
import imageJpg from '../images/sign-in.jpg';
import useEnterEmailController from './enter-email.controller';

export default function EnterEmailPage() {
  const { form, handleSubmit, handleBack } = useEnterEmailController();

  return (
    <Box>
      <NavigationBar leftContent={<BackButton onClick={handleBack} />} beta />
      <MainContentAuth>
        <ImageSectionAuthPages
          srcset={`${image400} 400w, ${image600} 600w, ${image800} 800w, ${image1000} 1000w, ${image1200} 1200w, ${image1600} 1600w, ${image2000} 2000w`}
          sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (max-width: 1200px) 1200px, (max-width: 1600px) 1600px, (min-width: 1601px) 2000px"
          src={imageJpg}
          alt="Welcome Back"
          display={{ base: 'none', lg: 'block' }}
        />
        <ContentContainerAuthPages
          position={{ base: 'initial', lg: 'relative' }}
          background="url(/images/footer-auth_3840px.svg)"
          backgroundRepeat="no-repeat"
          backgroundPosition={{
            base: 'center calc(100% - 86px)',
            lg: 'center calc(100% - 56px)',
            xxl: 'center calc(100% - 86px)',
          }}
          backgroundSize="auto 200px"
        >
          <Grid
            // In short screen sizes the content will have scroll
            // In higher screen sizes the content will be centered and the footer will be at the bottom
            gridTemplateRows="minmax(min-content, 1fr) min-content"
            alignItems="center"
            minHeight="full"
            overflow="auto"
          >
            <Box
              as="form"
              width={{ base: '100%', lg: '464px', xl: '529px' }}
              onSubmit={handleSubmit}
              px={{ base: 5, md: 8, lg: 0 }}
              pt={{ base: 5, lg: 0 }}
              mx="auto"
            >
              <FormContainerAuthPages>
                <Flex flexDir="column" gap="spacer-2">
                  <Text variant="text-header-4XL">
                    Welcome{' '}
                    <Text as="span" textStyle="text-header-4xl-v">
                      back!
                    </Text>
                  </Text>
                  <Text variant="text-body-regular">Enter your account email to login</Text>
                </Flex>
                <Divider />
                <FormControl id="email" isInvalid={!!form.formState.errors.email}>
                  <Input
                    id="email"
                    aria-label="Email"
                    variant="outline"
                    size="l"
                    placeholder={form.formState.errors.email ? '' : 'Enter email address'}
                    isInvalid={!!form.formState.errors.email}
                    {...form.register('email', {
                      required: 'Please type an email address',
                      validate: (email: string) => {
                        const isValidEmail = validationService.validateEmail(email);
                        return isValidEmail || 'Please provide a valid email address';
                      },
                    })}
                  />
                  {form.formState.errors.email?.message && (
                    <FormErrorMessage>
                      <InlineAlert status="error" mt="spacer-2">
                        <InlineAlert.Icon />
                        <InlineAlert.Message>{form.formState.errors.email.message}</InlineAlert.Message>
                      </InlineAlert>
                    </FormErrorMessage>
                  )}
                </FormControl>
                <Divider />
                <SubmitAccentButton
                  size="l"
                  isDisabled={!!form.formState.errors.email}
                  isLoading={form.formState.isSubmitting}
                >
                  Log in
                </SubmitAccentButton>
                <Text variant="text-body-meta-regular" w="100%" textAlign="center">
                  Don't have an account yet?{' '}
                  <Link as={ReactRouterLink} to={getRoute(ROUTE_SIGN_UP)} size="s" textDecoration="underline" isInline>
                    Get started
                  </Link>
                </Text>
              </FormContainerAuthPages>
              <Troubleshoot troubleshootIssue="Trouble signing in?" linkDisplay={{ base: 'block', md: 'inline' }} />
            </Box>
            <FooterAuth />
          </Grid>
        </ContentContainerAuthPages>
      </MainContentAuth>
    </Box>
  );
}
